import { z } from 'zod';

import { TripUpdateItemSchema } from './trip_update_event';

/**
 * `request_received` (Projeto B1, #3765): the requester's "pedido recebido".
 * voucher-events groups the `voucher.created` of one requester and booking for
 * 5 min and sends this language-neutral list; the conversation localizes it
 * (spec 2026-10-04-notification-preferences-b1 §8).
 */
const RequestReceivedItemSchema = z.object({
  kind: z.enum(['flight', 'hotel', 'car', 'other']),
  /** Language-neutral name: "GRU → FLN", the hotel name, a place. Never empty. */
  label: z.string().min(1),
  /** Wall-clock day of the start (`YYYY-MM-DD`); null when the voucher carries none. */
  dateLocal: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  /** The voucher came out of policy: it now waits for the team's approver. */
  awaitingApproval: z.boolean(),
});
export type RequestReceivedItem = z.infer<typeof RequestReceivedItemSchema>;

export const RequestReceivedMetadataSchema = z.object({
  /** The requester's OWN trip holding the vouchers; null when they booked for someone else. */
  tripId: z.string().min(1).nullable(),
  /** The trip name the traveler typed, else its destination; null without a trip. */
  tripTitle: z.string().min(1).nullable(),
  /** The purchase origin (`voucher.channel`): always the platform here (see TripUpdateMetadata). */
  originChannel: z.string().nullable().optional(),
  items: z.array(RequestReceivedItemSchema).min(1),
  // The e-mail in the nov/2026 design (#7101). Optional: an older producer omits them and the
  // e-mail keeps the layout of today.
  /** Who travels, only when the requester booked for someone else. PII. */
  travelerName: z.string().nullable().optional(),
  /** Where and when the trip goes (city; offset-less wall clocks), from the traveler's trip. */
  destination: z.string().nullable().optional(),
  startLocal: z.string().nullable().optional(),
  endLocal: z.string().nullable().optional(),
  /** When the window opened (ISO instant): the "Pedido recebido" step. */
  receivedAt: z.string().optional(),
  /** Some item came out of policy: the approval step says so. */
  outOfPolicy: z.boolean().optional(),
  /**
   * Each requested item as the trip e-mail draws it (`TripUpdateItem`, #7098), read from the
   * traveler's trip; `awaiting_approval` when it goes to an approver. Absent when trip-api had
   * none of the vouchers.
   */
  tripItems: z.array(TripUpdateItemSchema).min(1).optional(),
});
export type RequestReceivedMetadata = z.infer<typeof RequestReceivedMetadataSchema>;
